class CollectionTabRail extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('.pure-collection-tabs__rail');
    if (!this.rail) return;

    this.shopName = this.dataset.shopName || '';
    // Captured once: pushState below strips query params from the visible URL
    // as tabs swap, so re-reading location.search per click would lose this
    // after the first swap. See swapTo() for why it's needed at all.
    this.previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    this.onClick = this.onClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.rail.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    this.rail?.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPopState);
  }

  onClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a.pure-collection-tabs__link');
    if (!link) return;

    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;

    event.preventDefault();
    if (this.keyFor(url) === this.keyFor(new URL(window.location.href))) return;

    this.swapTo(url, link).then((ok) => {
      if (!ok) return;
      const pushedUrl = this.withPreview(url);
      history.pushState({ pureCollectionTab: true }, '', pushedUrl.pathname + pushedUrl.search);
    });
  }

  onPopState() {
    const url = new URL(window.location.href);
    this.swapTo(url, this.findLinkFor(url));
  }

  // Comparison key that ignores preview_theme_id -- it's carried on the visible
  // URL for shareability (see withPreview) but shouldn't affect identity checks.
  keyFor(url) {
    const clean = new URL(url.href);
    clean.searchParams.delete('preview_theme_id');
    return clean.pathname + clean.search;
  }

  withPreview(url) {
    const withParam = new URL(url.href);
    if (this.previewThemeId) withParam.searchParams.set('preview_theme_id', this.previewThemeId);
    return withParam;
  }

  findLinkFor(url) {
    const links = Array.from(this.querySelectorAll('a.pure-collection-tabs__link'));
    const target = this.keyFor(url);
    return (
      links.find((a) => this.keyFor(new URL(a.getAttribute('href'), window.location.origin)) === target) ||
      links.find((a) => new URL(a.getAttribute('href'), window.location.origin).pathname === url.pathname)
    );
  }

  async swapTo(url, link) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) {
      window.location.href = url.href;
      return false;
    }

    const collectionEl = container.querySelector('.collection');
    collectionEl?.classList.add('loading');

    // A layoutless collection view supplies the standard grid without needing
    // another template's generated section ID. It reuses the existing product
    // grid section, including its wholesale access checks.
    //
    // Theme-preview sessions (?preview_theme_id=) resolve by cookie for full
    // navigations but not for fetch(), which would otherwise 404 against the
    // live published theme instead. Carry it through explicitly so the swap is
    // reviewable on an unpublished theme, not just after launch.
    const fetchUrl = this.withPreview(url);
    // Reuse the existing beverage flavor cards without replacing the Shop page.
    // Other categories keep the guarded, layoutless product grid.
    const flavors = /\/collections\/beverages\/?$/.test(url.pathname);
    fetchUrl.searchParams.set('view', flavors ? 'pure-beverages' : 'pure-tab-grid');

    try {
      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error(`Tab fetch failed: ${response.status}`);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextContainer = flavors
        ? parsed.querySelector('[data-pure-variant-grid]')
        : parsed.getElementById('ProductGridContainer');
      if (!nextContainer) throw new Error('Tab fetch missing product cards');

      nextContainer.querySelectorAll('a[href]').forEach((anchor) => {
        const destination = new URL(anchor.getAttribute('href'), url);
        if (destination.origin !== url.origin || destination.searchParams.get('view') !== 'pure-tab-grid') return;
        destination.searchParams.delete('view');
        anchor.setAttribute('href', destination.pathname + destination.search + destination.hash);
      });
      container.innerHTML = flavors ? nextContainer.outerHTML : nextContainer.innerHTML;
      this.setActive(link || this.findLinkFor(url));
      return true;
    } catch (error) {
      console.error(error);
      window.location.href = url.href;
      return false;
    } finally {
      collectionEl?.classList.remove('loading');
    }
  }

  setActive(link) {
    this.querySelectorAll('a.pure-collection-tabs__link[aria-current]').forEach((a) => a.removeAttribute('aria-current'));
    if (!link) return;
    link.setAttribute('aria-current', 'page');

    const label = link.querySelector('.pure-collection-tabs__label')?.textContent?.trim();
    if (!label) return;

    document.title = this.shopName ? `${label} – ${this.shopName}` : label;
    this.announce(label);
  }

  announce(label) {
    let region = document.getElementById('CollectionTabAnnounce');
    if (!region) {
      region = document.createElement('div');
      region.id = 'CollectionTabAnnounce';
      region.setAttribute('aria-live', 'polite');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
    }
    region.textContent = `Showing ${label}`;
  }
}

if (!customElements.get('collection-tab-rail')) {
  customElements.define('collection-tab-rail', CollectionTabRail);
}
